import AppActionCreators from '~publish/legacy/composer/composer/action-creators/AppActionCreators'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'

import type { ComposerStore } from '../composerStore'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'

/**
 * Subscribe to Zustand's `selectedChannels` and reconcile Flux's selected
 * profiles to match, via `AppActionCreators.updateSelectedProfiles` — the one
 * Flux entry point built to take a plain idsToSelect/idsToUnselect diff. It
 * already carries the batch selection side effects (Facebook/Instagram
 * page-vs-group exclusivity, Pinterest board auto-select, Twitter Premium and
 * Mastodon character limits), so the mirror doesn't have to re-derive them.
 *
 * `ChannelSelector.tsx` is the only writer this mirrors: with
 * `composer-zustand-mode-state` on, its call sites write `channelSlice` only,
 * and this keeps Flux's `AppStore.getSelectedProfiles()` (still read from
 * elsewhere in the composer) in sync with it. Parity is asserted by the
 * existing `useChannelSelectionDrift` watcher (`channelSelectionDrift.ts`),
 * which already compares both stores' selected sets regardless of which side
 * changed first — no separate assertion is needed here.
 *
 * @returns An unsubscribe function; further Zustand writes will not reach Flux
 * after it is called.
 */
export function installZustandToChannelSelectionMirror(
  store: ComposerStore,
): () => void {
  const unsubscribe = store.subscribe((state, prev) => {
    if (state.selectedChannels === prev.selectedChannels) return
    if (isMirrorWriteInProgress()) return

    const fluxSelectedIds = new Set(
      AppStore.getSelectedProfiles().map((profile) => profile.id),
    )
    const zustandSelectedIds = new Set(state.selectedChannels)

    const idsToSelect = state.selectedChannels.filter(
      (id) => !fluxSelectedIds.has(id),
    )
    const idsToUnselect = [...fluxSelectedIds].filter(
      (id) => !zustandSelectedIds.has(id),
    )

    if (idsToSelect.length === 0 && idsToUnselect.length === 0) return

    runMirrorWrite(() => {
      AppActionCreators.updateSelectedProfiles(idsToSelect, idsToUnselect)
    })
  })

  return unsubscribe
}
